import { Magnification } from "@/app/activity/components/timeline/activityTimeline/type"
import {
  createActivityCommonStore,
  createActivityStore,
  createSessionStore,
} from "@/app/activity/store"
import BasicButton from "@/components/button/BasicButton"
import { Session } from "@/types/session"
import { D3DragEvent, drag } from "d3-drag"
import { select } from "d3-selection"

import { MouseEvent, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { createPortal } from "react-dom"

export default function TimelineAddBox({
  x,
  width,
  onClick,
  dateStart,
  dateFinish,
  disabled = false,
}: {
  x: number
  width: number
  onClick: (e: MouseEvent<SVGRectElement>) => void
  dateStart: Date
  dateFinish: Date
  disabled?: boolean
}) {
  const boxRef = useRef<SVGGElement>(null)

  const [localX, setLocalX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [prevMagnification, setPrevMagnification] = useState<number>(Magnification.INIT)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const { t, i18n } = useTranslation()

  const { setUpsertDialogOpen, setSession, setSessionGuideLine, sessionGuideLine } =
    createSessionStore((state) => ({
      setUpsertDialogOpen: state.setUpsertDialogOpen,
      setSession: state.setSession,
      setSessionGuideLine: state.setSessionGuideLine,
      sessionGuideLine: state.sessionGuideLine,
    }))

  const {
    range,
    timelineWidth,
    remainRightArea = 0,
    magnification,
  } = createActivityCommonStore((state) => ({
    range: state.range,
    remainRightArea: state.remainingArea,
    timelineWidth: state.timelineWidth,
    magnification: state.magnification,
  }))
  const { activityGuideLine } = createActivityStore((state) => ({
    activityGuideLine: state.activityGuideLine,
  }))

  const [clientWidth, setClientWidth] = useState(0)

  useEffect(() => {
    setClientWidth(buttonRef.current?.clientWidth || 0)
  }, [buttonRef.current, i18n.language])

  useEffect(() => {
    if (!boxRef.current) {
      return
    }

    select(boxRef.current as Element).call(
      drag()
        .on("drag", (e: D3DragEvent<SVGRectElement, unknown, unknown>) => {
          setIsDragging(true)
          setLocalX(e.x - x)
        })
        .on("end", () => {
          setIsDragging(false)
        }),
    )
  }, [boxRef.current, sessionGuideLine])

  useLayoutEffect(() => {
    if (!width) {
      setLocalX(0)
      setIsDragging(false)
    }
  }, [width])

  useLayoutEffect(() => {
    setIsDragging(false)

    if (prevMagnification < magnification) {
      setLocalX((prev) => prev * 2)
    } else if (prevMagnification > magnification) {
      setLocalX((prev) => prev / 2)
    }
    setPrevMagnification(magnification)
  }, [magnification, localX, activityGuideLine, x])

  useLayoutEffect(() => {
    const { dateStart: rangeStartDate, dateFinish: rangeFinishDate } = range
    const { startX, endX } = activityGuideLine || {}
    if (
      sessionGuideLine?.startX !== undefined &&
      sessionGuideLine?.endX !== undefined &&
      sessionGuideLine?.startX !== x + localX
    ) {
      if (isDragging) {
        const modifiedStartDate = new Date(
          ((localX + x - remainRightArea / 2) / timelineWidth) *
            ((rangeFinishDate?.getTime() as number) - (rangeStartDate?.getTime() as number)) +
            (rangeStartDate?.getTime() as number),
        )

        const modifiedEndDate = new Date(
          ((localX + x + width - remainRightArea / 2) / timelineWidth) *
            ((rangeFinishDate?.getTime() as number) - (rangeStartDate?.getTime() as number)) +
            (rangeStartDate?.getTime() as number),
        )

        if (startX && startX + remainRightArea / 2 >= x + localX) {
          setLocalX(startX - x + remainRightArea / 2)

          return
        }
        if (endX && endX + remainRightArea / 2 <= x + localX + width) {
          setLocalX(endX - x - width + remainRightArea / 2)

          return
        }

        setSessionGuideLine({
          ...sessionGuideLine,
          startDate: modifiedStartDate,
          endDate: modifiedEndDate,
          startX: x + localX,
          endX: x + localX + width,
        })

        return
      }
      setSessionGuideLine({
        ...sessionGuideLine,

        startX: x + localX,
        endX: x + localX + width,
      })
    }
  }, [x, localX, width, sessionGuideLine, isDragging])

  return (
    <>
      <svg>
        <g transform={`translate(${x + localX},0)`} onClick={onClick} ref={boxRef}>
          <rect
            width={Math.max(0, width)}
            height="100%"
            fill="rgb(47, 186, 195)"
            fillOpacity={0.2}
          />
        </g>
      </svg>
      {createPortal(
        <BasicButton
          className="TimelineAddBoxButton"
          ref={buttonRef}
          size="sm"
          type="default-outlined"
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            const formatLocalISO = (date: Date) => {
              const year = date.getFullYear()
              const month = String(date.getMonth() + 1).padStart(2, "0")
              const day = String(date.getDate()).padStart(2, "0")
              const hours = String(date.getHours()).padStart(2, "0")
              const minutes = String(date.getMinutes()).padStart(2, "0")
              const seconds = String(date.getSeconds()).padStart(2, "0")

              return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
            }

            setSession({
              dateStart: formatLocalISO(dateStart),
              dateFinish: formatLocalISO(dateFinish),
            } as Session)
            setUpsertDialogOpen(true)
          }}
          style={{
            left: x + localX + width / 2 - clientWidth / 2,
            top: "50%",
            transform: "translateY(-50%)",
          }}>
          {t("addSession").toUpperCase()}
        </BasicButton>,
        document.querySelector(".SessionTimelineContWrapper") as Element,
      )}
    </>
  )
}
